import React, { useCallback, useMemo } from 'react';

import { CommunityNavArticle } from '@lumapps/articles/types';
import { Community } from '@lumapps/communities/types';
import { Content } from '@lumapps/contents/types';
import { getCurrentInstance, getInstanceLanguages } from '@lumapps/instance/ducks/selectors';
import { currentLanguageSelector, getLanguageHeader } from '@lumapps/languages';
import { NavigationMap, NavigationMapPageReferenceType } from '@lumapps/navigation/types';
import { useSelector } from '@lumapps/redux/react';
import { useTranslate } from '@lumapps/translations';
import { useBooleanState } from '@lumapps/utils/hooks/useBooleanState';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { getMainNavigationMaps, updateMainNavigationMaps } from '../../api';
import { NODE_TEMP_ID_PREFIX } from '../../constants';
import { useConvertContentToNavigationNode } from '../../hooks/useConvertEntityToNavigationNode';
import { useNavigationAdmin } from '../../hooks/useNavigationAdmin';
import { NAVIGATION_ADMIN } from '../../keys';
import { isMoveNodeToSectionAllowed } from '../../utils';
import { SetNodeDestinationDialogProps } from '../SetNodeDestinationDialog';

/**
 * Hook to manage the `AddToMainNavigationDialog` component's state.
 */
export const useAddToMainNavigationDialog = ({
    entity,
    type,
}: {
    entity: Content | Community | CommunityNavArticle;
    type: NavigationMapPageReferenceType;
}) => {
    const { translateKey } = useTranslate();
    /** Get the current site id as the main nav is scoped to the current site */
    const { id: siteId } = useSelector(getCurrentInstance);
    const acceptLanguageHeader = useSelector(getLanguageHeader);
    /** list of instance languages */
    const languages = useSelector(getInstanceLanguages) || [];
    /** current user language */
    const currentLanguage = useSelector(currentLanguageSelector);

    /** Function to convert the given entity to a navigation node */
    const convertEntity = useConvertContentToNavigationNode({ languages, defaultLanguage: currentLanguage });

    /** State of the dialog */
    const [isOpen, toggleOpen] = useBooleanState(false);

    const getNavigationMap = React.useCallback(
        () => getMainNavigationMaps(siteId, acceptLanguageHeader),
        [acceptLanguageHeader, siteId],
    );
    const updateNavigationMap = React.useCallback(
        (navigationMap: NavigationMap) => updateMainNavigationMaps(siteId, navigationMap, acceptLanguageHeader),
        [siteId, acceptLanguageHeader],
    );

    /** Get and update state management */
    const {
        navigationMap,
        loadingState: navigationLoadingState,
        loadData: loadMainNavigation,
        onSave,
    } = useNavigationAdmin({
        getNavigationMap,
        updateNavigationMap,
        fetchOnMount: false,
    });

    /** Action on dialog open */
    const handleOpen = React.useCallback(async () => {
        /** Trigger the main navigation fetch */
        loadMainNavigation();
        /** Open the dialog */
        toggleOpen();
    }, [loadMainNavigation, toggleOpen]);

    /** Navigation node converted from given entity */
    const node = useMemo(() => {
        return {
            ...convertEntity({ entity, type }),
            /**
             * The `convertEntity` generates a new id every time it's called.
             * As the `entity` reference might change, we risk having the id being regenerated.
             * To avoid that, we set a fixed id based on the entity id.
             * The "Temp" prefix will allow us to find a remove the id on navigation save.
             */
            id: `${NODE_TEMP_ID_PREFIX}-${entity.id}`,
        };
    }, [convertEntity, entity, type]);

    /** Callback on dialog save */
    const handleSave: SetNodeDestinationDialogProps['onSave'] = useCallback(
        async (newNav) => {
            /** Save the new navigation. */
            if (navigationMap) {
                onSave({ ...navigationMap, children: newNav }, toggleOpen);
            }
        },
        [navigationMap, onSave, toggleOpen],
    );

    /**
     * Callback that will be executed in order to validate that a section can be moved into another.
     */
    const isSaveDisabled = useCallback(
        (depth, destination) => {
            const nodes = navigationMap?.children;

            if (!node || !nodes) {
                return { isDisabled: true };
            }
            return isMoveNodeToSectionAllowed({
                depth,
                node,
                section: !destination
                    ? { children: nodes, partialPermission: navigationMap.partialPermission }
                    : destination,
                editionRestrictionMessage: translateKey(NAVIGATION_ADMIN.NOT_ALLOWED_TO_ADD_CONTENT_TO_SECTION),
            });
        },
        [navigationMap, node, translateKey],
    );

    return {
        node,
        isOpen,
        loadingState: navigationLoadingState,
        nodes: navigationMap?.children,
        visibleByGroupReferences: navigationMap?.visibleByGroupReferences,
        languages,
        defaultLanguage: currentLanguage,
        handleOpen,
        onClose: toggleOpen,
        onSave: handleSave,
        dialogIsLoading: navigationLoadingState === BaseLoadingStatus.saving,
        isSaveDisabled,
    };
};
